import React, { useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import {
	useSharedPluginStateWithSelector,
	type NamedPluginStatesFromInjectionAPI,
} from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AgentManagedExtensionPlugin } from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';
import { getReferenceObjectFromContextIdentifier } from '../../common/referenceObjectAri';
import { resolveCurrentPageUrl } from '../../services/resolvePageUrl';

import { AimoConfigurationLoadState } from './AimoConfigurationLoadState';
import { ManagedBlockConfig } from './ManagedBlockConfig';
import { ManagedBlockConfigFrame } from './ManagedBlockConfigFrame';
import { getSmartCardProviderProduct } from './sources/smart-card-product';
import { useAiManagedObject } from './useAiManagedObject';
import { useConfiguredAgentManagedExtension } from './useConfiguredAgentManagedExtension';
import { usePopupTargetElement } from './usePopupTargetElement';

interface AgentManagedExtensionConfigurePopupProps {
	api: ExtractInjectionAPI<AgentManagedExtensionPlugin> | undefined;
	cloudId?: string;
	contentType?: string;
	editorView: EditorView;
	mountPoint?: HTMLElement;
}

const POPUP_STYLE = { width: 500 } as const;
const POPUP_OFFSET: [number, number] = [0, 8];

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<AgentManagedExtensionPlugin>,
		'contextIdentifier'
	>,
) => ({
	contextIdentifierProvider: states.contextIdentifierState?.contextIdentifierProvider,
});

type ConfigurationPanelProps = React.ComponentProps<typeof ManagedBlockConfig> & {
	configurationLoadError: string | null;
	isLoadingConfiguration: boolean;
	onRetryConfigurationLoad: () => void;
};

const ConfigurationPanel = ({
	configurationLoadError,
	isLoadingConfiguration,
	onRetryConfigurationLoad,
	...configProps
}: ConfigurationPanelProps): JSX.Element => {
	if (isLoadingConfiguration) {
		return (
			<ManagedBlockConfigFrame testId="agent-managed-extension-configure-panel-loading">
				<AimoConfigurationLoadState
					status="loading"
					testId="agent-managed-extension-configure-panel-loading-state"
					variant="panel"
				/>
			</ManagedBlockConfigFrame>
		);
	}

	if (configurationLoadError) {
		return (
			<ManagedBlockConfigFrame testId="agent-managed-extension-configure-panel-load-error">
				<AimoConfigurationLoadState
					errorMessage={configurationLoadError}
					onRetry={onRetryConfigurationLoad}
					status="failed"
					testId="agent-managed-extension-configure-panel-load-error-state"
					variant="panel"
				/>
			</ManagedBlockConfigFrame>
		);
	}

	return <ManagedBlockConfig {...configProps} />;
};

/**
 * Renders the inline configuration popup for the agent-managed extension
 * currently in (or around) the selection — when the plugin has been told to open the popup.
 */
export const AgentManagedExtensionConfigurePopup = ({
	api,
	cloudId,
	contentType,
	editorView,
	mountPoint,
}: AgentManagedExtensionConfigurePopupProps): React.JSX.Element | null => {
	const intl = useIntl();
	const popupContentRef = useRef<HTMLDivElement>(null);
	const configurationAriaLabel = intl.formatMessage(messages.configurationAriaLabel);
	const { contextIdentifierProvider } = useSharedPluginStateWithSelector(
		api,
		['contextIdentifier'],
		selector,
	);
	const {
		aimoId,
		aimoLoadStatus,
		blockElement,
		cachedAimo,
		configLocalId,
		found,
		foundPos,
		isOpen,
		templateConfig,
	} = useConfiguredAgentManagedExtension({ api, editorView });
	const targetElement = usePopupTargetElement({ blockElement, isOpen });
	const referenceObject = getReferenceObjectFromContextIdentifier({
		cloudId,
		contentType,
		contextIdentifierProvider,
	});
	const currentPageSourceUrl = useMemo(() => resolveCurrentPageUrl(), []);
	const smartCardProviderProduct = getSmartCardProviderProduct(contextIdentifierProvider?.product);
	const {
		aimoData,
		configurationLoadError,
		error,
		handleCancel,
		handleClearError,
		handleRetryConfigurationLoad,
		handleSave,
		initialConfiguration,
		isLoadingConfiguration,
		isSaving,
	} = useAiManagedObject({
		aimoId,
		aimoLoadStatus,
		cachedAimo,
		cloudId,
		configLocalId,
		contextIdentifierProvider,
		contentType,
		editorView,
		foundPos,
		isOpen,
		templateConfig,
	});
	const panelInitialConfiguration = useMemo(() => {
		const [source] = aimoData?.config?.sources ?? [];
		const isCurrentPageSource =
			Boolean(currentPageSourceUrl) &&
			aimoData?.config?.sources?.length === 1 &&
			source?.url === currentPageSourceUrl;

		return isCurrentPageSource
			? {
					...initialConfiguration,
					sourceMode: 'current-page' as const,
				}
			: initialConfiguration;
	}, [aimoData?.config?.sources, currentPageSourceUrl, initialConfiguration]);
	const showInitialSourceDisclaimer =
		!aimoId &&
		panelInitialConfiguration.mode === 'configuration' &&
		panelInitialConfiguration.sourceMode === 'current-page' &&
		Boolean(currentPageSourceUrl);
	const shouldUsePopupShortcutGuard = isLoadingConfiguration || Boolean(configurationLoadError);

	useEffect(() => {
		if (!isOpen || !shouldUsePopupShortcutGuard) {
			return;
		}

		const popupContentElement = popupContentRef.current;
		const ownerWindow = popupContentElement?.ownerDocument.defaultView;
		if (!popupContentElement || !ownerWindow) {
			return;
		}

		const stopShortcutPropagation = (event: KeyboardEvent, shouldPreventDefault = false) => {
			if (shouldPreventDefault) {
				event.preventDefault();
			}
			event.stopPropagation();
			event.stopImmediatePropagation();
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				!(event.target instanceof ownerWindow.Node) ||
				!popupContentElement.contains(event.target)
			) {
				return;
			}

			const isCloseShortcut = event.key === 'Escape' || event.key === 'Esc';
			const isSaveShortcut = event.key === 'Enter' && (event.metaKey || event.ctrlKey);

			if (isCloseShortcut) {
				stopShortcutPropagation(event);
				if (!isSaving) {
					handleCancel();
				}
				return;
			}

			if (isSaveShortcut) {
				stopShortcutPropagation(event, true);
			}
		};

		ownerWindow.addEventListener('keydown', handleKeyDown, true);

		return () => {
			ownerWindow.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [handleCancel, isOpen, isSaving, shouldUsePopupShortcutGuard]);

	if (!isOpen || !found || !targetElement) {
		return null;
	}

	return (
		<Popup
			alignX="center"
			alignY="bottom"
			target={targetElement}
			mountTo={mountPoint}
			zIndex={100}
			fitWidth={500}
			fitHeight={250}
			preventOverflow={true}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- `Popup` accepts only an inline style prop for its outer wrapper
			style={POPUP_STYLE}
			offset={POPUP_OFFSET}
			ariaLabel={configurationAriaLabel}
		>
			<div data-no-extension-select="true" ref={popupContentRef}>
				<ConfigurationPanel
					currentPageSourceUrl={currentPageSourceUrl}
					initialConfiguration={panelInitialConfiguration}
					isVisible={isOpen}
					persistedConfig={aimoData?.config}
					onSave={handleSave}
					onCancel={handleCancel}
					onClearError={handleClearError}
					onRetryConfigurationLoad={handleRetryConfigurationLoad}
					configurationLoadError={configurationLoadError}
					isLoadingConfiguration={isLoadingConfiguration}
					isSaving={isSaving}
					error={error}
					aimoData={aimoData}
					cloudId={referenceObject?.cloudId}
					smartCardProviderProduct={smartCardProviderProduct}
					showInitialSourceDisclaimer={showInitialSourceDisclaimer}
				/>
			</div>
		</Popup>
	);
};
